import axios from "axios";
import { useAuthStore } from "./store/auth";

// Бэкенд на том же хосте, что и фронтенд, на фиксированном порту. Вывод origin
// из window.location вместо фиксации хоста при сборке позволяет одной сборке
// работать через VPN, LAN и localhost.
const defaultApiUrl = () =>
    typeof window !== "undefined" && window.location?.hostname
        ? `${window.location.protocol}//${window.location.hostname}:8001`
        : "http://localhost:8001";

export const API_BASE_URL = import.meta.env.VITE_API_URL || defaultApiUrl();

const api = axios.create({
    baseURL: API_BASE_URL,
});

// Аватар (/users/{id}/avatar) — фиксированный путь, не меняющийся при переуплоаде:
// без cache-bust ни Vue-биндинг :src (та же строка, DOM не обновится), ни HTTP-кэш
// браузера (тот же URL) не покажут новую картинку без жёсткой перезагрузки.
const AVATAR_PATH_PATTERN = /^\/users\/\d+\/avatar$/;

export function resolveMediaUrl(url: string | null | undefined): string | null | undefined {
    if (!url || !url.startsWith("/")) return url;
    let absolute = `${API_BASE_URL}${url}`;
    if (AVATAR_PATH_PATTERN.test(url)) {
        absolute += `?v=${Date.now()}`;
    }
    // <img src> не может отправить заголовок Authorization, поэтому для приватных
    // камер токен передаётся query-параметром (бэкенд принимает и так, и так,
    // см. core/deps.py); для непубличных ресурсов это безвредно. То же касается
    // <video src>/<a download> для таймлапсов и админских превью/скачиваний.
    const needsAuthFallback =
        (url.startsWith("/cameras/") && url.endsWith("/snapshot")) ||
        (url.startsWith("/admin/cameras/") && url.endsWith("/snapshot-download")) ||
        (url.startsWith("/timelapses/videos/") && url.endsWith("/download")) ||
        url.startsWith("/admin/storage/object") ||
        url.startsWith("/admin/feedback/images/") ||
        url.startsWith("/admin/fog-experiment/images/");
    if (needsAuthFallback) {
        const auth = useAuthStore();
        if (auth.accessToken) {
            const separator = absolute.includes("?") ? "&" : "?";
            return `${absolute}${separator}token=${encodeURIComponent(auth.accessToken)}`;
        }
    }
    return absolute;
}

api.interceptors.request.use((config) => {
    const auth = useAuthStore();
    if (auth.accessToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${auth.accessToken}`;
    }
    return config;
});

// Один рефреш на всех (дедуп и решение, когда стирать токены, - в
// store/auth.ts::refreshAccessToken). Сам запрос /auth/refresh интерцептор
// не трогает: 401 там значит "refresh-токен недействителен", а не "попробуй
// обновиться ещё раз".
const REFRESH_PATH = "/auth/refresh";

api.interceptors.response.use(
    (resp) => resp,
    async (error) => {
        const config = error.config;
        if (error.response?.status !== 401 || !config || config.url === REFRESH_PATH || config._retried) {
            return Promise.reject(error);
        }
        const auth = useAuthStore();
        if (!auth.refreshToken) return Promise.reject(error);
        const result = await auth.refreshAccessToken();
        if (result !== "ok") return Promise.reject(error);
        config._retried = true;
        return api(config);
    }
);

export default api;
